import React, { useContext } from 'react';
import { AppContext } from '@thd-nucleus/app-render';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Typography, Button } from '@one-thd/sui-atomic-components';
import { Printer, ShareWeb } from '@one-thd/sui-icons';
import { CardType } from '../../../constants/payments';
import { TextButton } from '../../shared/text-button/text-button';
import { usePayments } from '../../../hooks/use-payments';
import { useModal } from '../../../hooks/use-modal';
import { getFileName, saveFile, printFile } from './payments-header-helpers';
import { b2bPaymentsApi } from '../../../api/b2b/payments';
import { setOverlayEvent } from '../../../utils/analytics';
import {
  IStopAllAuthProps,
  StopAllAuthModal,
} from '../stop-all-auth-modal/stop-all-auth-modal';
import {
  setOtherButtonSubmissionAnalytics,
  setTopLinksAnalytics,
} from '../../../utils/payments-analytics';
import {
  ERR_UNABLE_TO_SAVE_PAYMENT,
  UNABLE_TO_COMPLETE_ERROR,
} from '../../../constants/response-error-messages';
import {
  STOP_ALL_AUTH_SUCCESS_MSG,
  EXPORT,
  PRINT,
} from '../../../constants/response-success-messages';
import { useLoyaltyMembershipDetails } from '../../../hooks/use-loyalty-membership-details';

interface IPaymentHeaderSecondaryButtons {
  cardType: CardType;
  showStopAuthBtn?: boolean;
  onActionSuccess: (paymentId: string, message: string) => void;
  onActionFailure: (paymentId: string, message: string) => void;
}

const cardTypesTitleSingular: Record<CardType, string> = {
  [CardType.CREDIT_CARD]: 'Credit Card',
  [CardType.PRO_XTRA_DOLLARS]: 'ProxPerksCards',
  [CardType.GIFT_CARD]: 'Gift Card',
  [CardType.PAYPAL_ACCOUNT]: 'PayPal',
  [CardType.CHECKING_ACCOUNT]: 'Checking Account',
  [CardType.KEY_TAG]: 'Keytag',
  [CardType.PRO_HOUSE_ACCOUNT]: 'Pro Trade Credit',
} as const;

export const PaymentsHeaderSecondaryButtons = ({
  cardType,
  showStopAuthBtn = false,
  onActionFailure,
  onActionSuccess,
}: IPaymentHeaderSecondaryButtons) => {
  const { instance } = useContext(AppContext);
  const isB2B = instance?.customer?.type === 'b2b';
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const { paginationInfo } = usePayments();
  const isDesktop = channel === 'desktop';
  const { openModal, setComponentProps, hideModal } = useModal();
  const { hasProAllowance } = useLoyaltyMembershipDetails();

  const printAlertMsg = `${cardTypesTitleSingular[cardType]} ${PRINT.message}`;
  const exportAlertMsg = `${cardTypesTitleSingular[cardType]} ${EXPORT.message}`;

  const cardLength: Record<CardType, number> = {
    [CardType.CREDIT_CARD]: paginationInfo?.totalCreditCards || 0,
    [CardType.PRO_XTRA_DOLLARS]: paginationInfo?.totalProXtraDollars || 0,
    [CardType.GIFT_CARD]: paginationInfo?.totalGiftCards || 0,
    [CardType.PAYPAL_ACCOUNT]: paginationInfo?.totalPayPalAccounts || 0,
    [CardType.CHECKING_ACCOUNT]: paginationInfo?.totalCheckingAccounts || 0,
    [CardType.KEY_TAG]: paginationInfo?.totalKeyTags || 0,
    [CardType.PRO_HOUSE_ACCOUNT]: paginationInfo?.totalLinesOfCredit || 0,
  } as const;

  const stopAllAuthorization = () => {
    b2bPaymentsApi
      .stopAllAuthorization()
      .then(() => {
        setOtherButtonSubmissionAnalytics(
          'credit cards: credit cards: stop all remote purchases success',
        );
        if (onActionSuccess)
          onActionSuccess('', STOP_ALL_AUTH_SUCCESS_MSG.message);
      })
      .catch(() => {
        if (onActionFailure)
          onActionFailure('', ERR_UNABLE_TO_SAVE_PAYMENT.message);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
        hideModal();
      });
  };

  const onClickNo = () => {
    hideModal();
    setOtherButtonSubmissionAnalytics(
      'credit cards: stop all remote purchases overlay: no button',
    );
  };

  const onShowStopAllAuthorization = () => {
    setTopLinksAnalytics('stop all authorizations');
    setOverlayEvent('stop remote purchases for all credit cards');
    const props: IStopAllAuthProps = {
      isLoading: false,
      onConfirmStopAllAuth: stopAllAuthorization,
      onClickNo,
      hasBenefit: hasProAllowance,
    };
    openModal(StopAllAuthModal, props);
  };

  const onExportToCSV = () => {
    setComponentProps({ isLoading: true });
    const filename = getFileName(cardType);
    b2bPaymentsApi
      .exportPaymentCardsToCSV(cardType, filename)
      .then((response) => {
        return response.data;
      })
      .then((data) => {
        onActionSuccess('', exportAlertMsg);
        saveFile(data, `${filename}.csv`);
      })
      .catch(() => {
        onActionFailure('', UNABLE_TO_COMPLETE_ERROR.message);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
      });
  };

  const onPrintToPDF = () => {
    setComponentProps({ isLoading: true });
    const filename = getFileName(cardType);

    b2bPaymentsApi
      .printPaymentCardsToPDF(cardType, filename)
      .then((response) => {
        return response.data;
      })
      .then((data) => {
        onActionSuccess('', printAlertMsg);
        printFile(data, `${filename}.pdf`);
      })
      .catch(() => {
        onActionFailure('', UNABLE_TO_COMPLETE_ERROR.message);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
      });
  };

  return (
    <div className="sui-mb-4">
      {isB2B && !!cardLength[cardType] && (
        <div className="sui-flex sui-items-center sui-justify-between">
          {showStopAuthBtn && (
            <TextButton
              className={`${isDesktop ? 'sui-mr-14' : 'sui-ml-0'}`}
              onClick={onShowStopAllAuthorization}
            >
              Stop Remote Purchases
            </TextButton>
          )}
          {!isConsumerApp && (
            <div className="sui-flex sui-flex-1 sui-justify-end">
              <Button
                className={`${
                  isDesktop ? 'sui-mr-14' : 'sui-ml-auto'
                } sui-block hover:sui-fill-brand sui-flex sui-gap-1`}
                onClick={onExportToCSV}
              >
                <ShareWeb />
                <Typography
                  className="sui-font-regular sui-text-base"
                  decoration="underline"
                >
                  Export
                </Typography>
              </Button>

              {isDesktop && (
                <Button
                  onClick={onPrintToPDF}
                  className="hover:sui-fill-brand sui-flex sui-gap-1"
                >
                  <Printer size="regular" />
                  <Typography
                    className="sui-font-regular sui-text-base"
                    decoration="underline"
                  >
                    Print
                  </Typography>
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
